import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './Screensaver.module.css';

const IDLE_MS = 20_000;
const SPEED = 2.2; // px per frame, each axis
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];

// DVD-logo style: the (white) logo takes a new tint on every wall bounce
const TINTS = [
  'none',
  'sepia(1) saturate(6) hue-rotate(5deg)', /* gold */
  'sepia(1) saturate(6) hue-rotate(100deg)', /* green */
  'sepia(1) saturate(6) hue-rotate(170deg)', /* blue */
  'sepia(1) saturate(6) hue-rotate(230deg)', /* purple */
  'sepia(1) saturate(6) hue-rotate(290deg)', /* pink */
];

/**
 * Screensaver — bouncing-logo Easter egg after IDLE_MS of inactivity.
 *
 * Bounces the white Boombox logo around a black screen, DVD-logo style,
 * changing tint on every wall hit. Any key, click, tap, or mouse move
 * dismisses it. It won't kick in while a <video> is playing, while anything
 * is fullscreen (e.g. a YouTube embed), or while focus is inside an iframe
 * (someone interacting with an embed's own controls).
 */
const Screensaver = () => {
  const [active, setActive] = useState(false);
  const logoRef = useRef(null);
  const rafRef = useRef(null);
  const stateRef = useRef(null);

  const mediaIsBusy = useCallback(() => {
    // document.hidden (not hasFocus) — hasFocus is unreliably false on
    // mobile even when the page is clearly in the foreground
    if (document.hidden || document.fullscreenElement) return true;
    if (document.activeElement?.tagName === 'IFRAME') return true;
    return [...document.querySelectorAll('video')].some((v) => !v.paused);
  }, []);

  // ── Idle timer — while inactive, wait for IDLE_MS of silence ──
  useEffect(() => {
    if (active) return undefined;
    let timer;

    const arm = () => {
      // Activity on OUR page proves the user has left any embed they tapped
      // earlier — release the iframe's stale focus so it can't block us
      if (document.activeElement?.tagName === 'IFRAME') {
        document.activeElement.blur();
      }
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (mediaIsBusy()) arm();
        else setActive(true);
      }, IDLE_MS);
    };

    arm();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, arm, { passive: true }));
    document.addEventListener('visibilitychange', arm);
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, arm));
      document.removeEventListener('visibilitychange', arm);
    };
  }, [active, mediaIsBusy]);

  // ── While active — bounce loop + dismiss on any activity ──
  useEffect(() => {
    if (!active) return undefined;

    const logo = logoRef.current;
    const size = () => ({
      w: window.innerWidth - (logo?.offsetWidth ?? 0),
      h: window.innerHeight - (logo?.offsetHeight ?? 0),
    });

    stateRef.current = {
      x: Math.random() * size().w,
      y: Math.random() * size().h,
      dx: Math.random() < 0.5 ? SPEED : -SPEED,
      dy: Math.random() < 0.5 ? SPEED : -SPEED,
      tint: 0,
    };

    const step = () => {
      const s = stateRef.current;
      const { w, h } = size();
      let bounced = false;

      s.x += s.dx;
      s.y += s.dy;
      if (s.x <= 0 || s.x >= w) { s.dx = -s.dx; s.x = Math.max(0, Math.min(s.x, w)); bounced = true; }
      if (s.y <= 0 || s.y >= h) { s.dy = -s.dy; s.y = Math.max(0, Math.min(s.y, h)); bounced = true; }
      if (bounced) s.tint = (s.tint + 1) % TINTS.length;

      if (logo) {
        logo.style.transform = `translate(${s.x}px, ${s.y}px)`;
        logo.style.filter = TINTS[s.tint];
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);

    const dismiss = () => setActive(false);
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, dismiss, { passive: true }));
    return () => {
      cancelAnimationFrame(rafRef.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, dismiss));
    };
  }, [active]);

  // Respect reduced-motion preferences — no screensaver at all
  if (typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return null;
  }

  if (!active) return null;

  return (
    <div className={styles.overlay} aria-hidden="true">
      <img
        ref={logoRef}
        src={`${process.env.PUBLIC_URL}/real_images/logo_black.png`}
        alt=""
        className={styles.logo}
        draggable={false}
      />
    </div>
  );
};

export default Screensaver;
